<template>
  <AppPageContainer :custom="1600" :min-height="0">
    <AppPageHeader design="new" title="Insurance updates" compact />
    <AppTabs design="new" refined weight="medium" :tabs="tabs" :value="filters.tab" @input="changeTab" />
    <main class="ui-page">
      <AppFilterToolbar>
        <template #primary><AppInput id="insurance-update-search" :value="filters.search" design="new" type="search" placeholder="Client or group" aria-label="Search" maxlength="255" @input="scheduleSearch" /></template>
        <AppSelect :value="filters.email_outcome" design="new" input-id="insurance-update-email" aria-label="Email status" :options="outcomeOptions" :reduce="option => option.value" label="label" placeholder="Email status" @input="changeFilter('email_outcome', $event)" />
        <AppSelect :value="filters.whatsapp_outcome" design="new" input-id="insurance-update-whatsapp" aria-label="WhatsApp status" :options="outcomeOptions" :reduce="option => option.value" label="label" placeholder="WhatsApp status" @input="changeFilter('whatsapp_outcome', $event)" />
        <AppSelect :value="filters.reason" design="new" truncate-selected input-id="insurance-update-reason" aria-label="Reason" :options="reasonOptions" :reduce="option => option.value" label="label" placeholder="Reason" @input="changeFilter('reason', $event)"><template #custom-selected-option="option"><AppTableText :text="option.label" /></template></AppSelect>
        <template #actions>
          <AppIconButton v-if="canEdit" label="Preview or send updates" tooltip-placement="left" @click="showRunModal = true"><i class="pi pi-send" aria-hidden="true" /></AppIconButton>
          <AppIconButton label="Refresh" tooltip-placement="left" :disabled="loading" @click="load"><i class="pi pi-refresh" aria-hidden="true" /></AppIconButton>
        </template>
        <template v-if="hasFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
      </AppFilterToolbar>
      <AppErrorState v-if="error" :message="error" icon-only @retry="load" />
      <AppContentSkeleton v-else-if="loading" label="Loading clients" :columns="columns" min-width="1200px" />
      <AppCard v-else-if="items.length" design="new" variant="table" header-density="compact" :title="activeTab.label" :count="count">
        <ClientTable :rows="items" :loading="loading" :columns="columns" @open="openClient" />
      </AppCard>
      <AppNoResults v-else design="new" title="No clients found" text="" />
      <AppPagination v-if="!error && !loading && loaded" design="new" placement="embedded" :page="filters.page" :count="count" :visible-count="items.length" :page-size="40" :loading="loading" @change="changePage" />
    </main>
    <RunModal v-if="showRunModal && canEdit" @close="showRunModal = false" @completed="onRunCompleted" />
    <ClientDetailModal v-if="modalClientId !== null" :client-id="modalClientId" @close="modalClientId = null" @updated="load" />
  </AppPageContainer>
</template>
<script>
import { getGroundOpsInsuranceUpdateClients } from '@/http'
import AppTabs from '@/components/AppTabs'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import AppTableText from '@/components/AppTableText.vue'
import { groundOpsKey } from '@/utils/groundOpsAccess'
import { REASONS } from './model'
import ClientDetailModal from './ClientDetailModal'
import ClientTable from './ClientTable'
import RunModal from './RunModal'

const TABS = [
  { value: 'upcoming', label: 'Upcoming', description: 'Clients who arrive soon and still have a message to receive.' },
  { value: 'attention', label: 'Needs attention', description: 'The arrival date has passed and no message was sent.' },
  { value: 'completed', label: 'Completed', description: 'Both the email and the WhatsApp message were sent.' }
]
const DEFAULT_FILTERS = { tab: 'upcoming', search: '', email_outcome: null, whatsapp_outcome: null, reason: null, page: 1 }

export default {
  name: 'InsuranceUpdatesList',
  components: { AppTabs, AppContentSkeleton, AppErrorState, AppIconButton, AppTableText, ClientDetailModal, ClientTable, RunModal },
  data() {
    return {
      items: [],
      count: 0,
      loaded: false,
      loading: false,
      error: '',
      modalClientId: null,
      showRunModal: false,
      searchTimer: null,
      requestNumber: 0,
      filters: { ...DEFAULT_FILTERS },
      tabs: TABS,
      columns: [
        { key: 'client', label: 'Client', width: '24%' },
        { key: 'group', label: 'Group', width: '22%' },
        { key: 'arrival', label: 'Arrival', width: '14%' },
        { key: 'channels', label: 'Email / WhatsApp', width: '14%' },
        { key: 'language', label: 'Language', width: '12%' },
        { key: 'checked', label: 'Last checked', width: '14%' }
      ],
      reasonOptions: Object.keys(REASONS).map(value => ({ value, label: REASONS[value] })),
      outcomeOptions: [
        { value: 'sent', label: 'Sent' },
        { value: 'not_sent', label: 'Not sent yet' },
        { value: 'issue', label: 'Has a problem' }
      ]
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](groundOpsKey('insurance_updates', 'edit')) },
    activeTab() { return this.tabs.find(tab => tab.value === this.filters.tab) || this.tabs[0] },
    hasFilters() { return Boolean(this.filters.search || this.filters.email_outcome || this.filters.whatsapp_outcome || this.filters.reason) }
  },
  created() { this.load() },
  beforeDestroy() { clearTimeout(this.searchTimer); this.requestNumber += 1 },
  methods: {
    onRunCompleted(mode) {
      if (mode === 'dry-run') return
      this.load()
    },
    async load() {
      const requestNumber = ++this.requestNumber
      this.loading = true
      this.error = ''
      try {
        const response = await getGroundOpsInsuranceUpdateClients({
          page: this.filters.page,
          tab: this.filters.tab,
          search: this.filters.search || undefined,
          email_outcome: this.filters.email_outcome || undefined,
          whatsapp_outcome: this.filters.whatsapp_outcome || undefined,
          reason: this.filters.reason || undefined
        })
        if (requestNumber !== this.requestNumber) return
        this.items = response.data.results
        this.count = response.data.count
        this.loaded = true
      } catch (error) {
        if (requestNumber !== this.requestNumber) return
        this.error = 'Could not load the clients. Try again.'
      } finally {
        if (requestNumber === this.requestNumber) this.loading = false
      }
    },
    scheduleTextFilter(key, value) {
      this.filters[key] = (value || '').trimStart()
      this.filters.page = 1
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.load, 350)
    },
    scheduleSearch(value) { this.scheduleTextFilter('search', value) },
    changeTab(tab) { this.filters.tab = tab; this.filters.page = 1; this.load() },
    changeFilter(key, value) { this.filters[key] = value || null; this.filters.page = 1; this.load() },
    changePage(page) { this.filters.page = page; this.load() },
    clearFilters() {
      clearTimeout(this.searchTimer)
      this.filters = { ...DEFAULT_FILTERS, tab: this.filters.tab }
      this.load()
    },
    openClient(id) { this.modalClientId = id }
  }
}
</script>
